/**
 * Stripe Pricing Table mount / unlock for the public signup page.
 */

import { ensureScript } from "../utils/cdn-loader.js";
import { pricingTableIdForCurrency } from "./pricing-table-by-currency.js";

const STRIPE_PRICING_TABLE_SCRIPT = "https://js.stripe.com/v3/pricing-table.js";

async function ensureStripePricingTableCustomElement() {
  if (window.customElements?.get("stripe-pricing-table")) {
    return;
  }
  await ensureScript(STRIPE_PRICING_TABLE_SCRIPT);
}

/**
 * @param {string} [currency]
 * @param {{ onboardingId?: string, contactEmail?: string }} [unlock]
 * @returns {Promise<boolean>}
 */
export async function mountSignupPricingTablePreview(currency = "BRL", unlock = {}) {
  const host = document.getElementById("signupPricingTableHost");
  if (!host) return false;

  const pricingTableId = pricingTableIdForCurrency(currency);
  const publishableKey = String(window.CONFIG?.STRIPE_PUBLISHABLE_KEY || "").trim();
  if (!pricingTableId || !publishableKey) {
    host.classList.add("pricing-locked");
    host.classList.remove("pricing-unlocked");
    const existing = document.getElementById("signupStripePricingTable");
    existing?.remove();
    return false;
  }

  try {
    await ensureStripePricingTableCustomElement();
  } catch (error) {
    window.Logger?.warn?.("Could not load pricing table script", error);
    return false;
  }

  // Remount when currency/table id changes (Stripe element does not hot-swap cleanly).
  let tbl = document.getElementById("signupStripePricingTable");
  const currentId = tbl?.getAttribute("pricing-table-id") || "";
  if (tbl && currentId !== pricingTableId) {
    tbl.remove();
    tbl = null;
  }
  if (!tbl) {
    tbl = document.createElement("stripe-pricing-table");
    tbl.id = "signupStripePricingTable";
    host.insertBefore(tbl, host.firstChild);
  }
  tbl.setAttribute("pricing-table-id", pricingTableId);
  tbl.setAttribute("publishable-key", publishableKey);

  const onboardingId = String(unlock.onboardingId || "").trim();
  if (onboardingId) {
    tbl.setAttribute("client-reference-id", onboardingId);
    const email = String(unlock.contactEmail || "").trim();
    if (email) tbl.setAttribute("customer-email", email);
    host.classList.remove("pricing-locked");
    host.classList.add("pricing-unlocked");
  }

  return true;
}

/**
 * @param {string} onboardingId
 * @param {string} [contactEmail]
 * @returns {boolean}
 */
export function unlockSignupPricingTable(onboardingId, contactEmail) {
  const host = document.getElementById("signupPricingTableHost");
  const tbl = document.getElementById("signupStripePricingTable");
  if (!host || !tbl) return false;
  tbl.setAttribute("client-reference-id", onboardingId);
  if (contactEmail) tbl.setAttribute("customer-email", contactEmail);
  host.classList.remove("pricing-locked");
  host.classList.add("pricing-unlocked");
  return true;
}

/**
 * Relock the pricing table host (e.g. after country/currency change).
 */
export function lockSignupPricingTable() {
  const host = document.getElementById("signupPricingTableHost");
  host?.classList.add("pricing-locked");
  host?.classList.remove("pricing-unlocked");
}
